import type {
  ICartService,
  WorkflowCart,
  WorkflowType,
  AddToCartParams,
  AddToCartResult,
  RemoveFromCartParams,
  RemoveFromCartResult,
  ClearCartResult,
  GetCartResult,
  BulkAllocateParams,
  BulkAllocateResult,
  ScanningStatus,
} from './types';
import { httpClient } from '@/utils/httpClient';
import { API_CONFIG } from '@/config/api';

/**
 * CartService handles database-backed cart operations for the Workflow.
 * All cart state is persisted in the database, enabling:
 * - Persistent carts across sessions and devices
 * - Real-time stock reservation to prevent double-issuance
 */
export class CartService implements ICartService {
  private get baseUrl(): string {
    return `${API_CONFIG.BASE_URL}${API_CONFIG.DATA_API_BASE_PATH}`;
  }

  /**
   * Get the cart for a specific customer and workflow type.
   * Returns cart metadata and grouped cart items.
   */
  async getCart(customerId: string, workflowType: WorkflowType = 'issue'): Promise<GetCartResult> {
    const response = await httpClient.post<GetCartResult>(`${this.baseUrl}/rpc/get_workflow_cart`, {
      p_customer_id: customerId,
      p_workflow_type: workflowType,
    });
    return response;
  }

  /**
   * Get all active carts for the property book view.
   */
  async getAllActiveCarts(): Promise<WorkflowCart[]> {
    const response = await httpClient.get<WorkflowCart[]>(
      `${this.baseUrl}/workflow_carts?select=*`,
    );
    return response;
  }

  /**
   * Add assets to the cart.
   * Assets are immediately reserved in the database.
   */
  async addToCart(params: AddToCartParams): Promise<AddToCartResult> {
    const response = await httpClient.post<AddToCartResult>(
      `${this.baseUrl}/rpc/add_to_workflow_cart`,
      {
        p_customer_id: params.customerId,
        p_asset_ids: params.assetIds,
        p_item_source: params.itemSource ?? 'ie',
        p_ie_kit_id: params.ieKitId ?? null,
        p_workflow_type: params.workflowType ?? 'issue',
      },
    );
    return response;
  }

  /**
   * Remove assets from the cart.
   * Releases reservations for removed assets.
   */
  async removeFromCart(params: RemoveFromCartParams): Promise<RemoveFromCartResult> {
    const response = await httpClient.post<RemoveFromCartResult>(
      `${this.baseUrl}/rpc/remove_from_workflow_cart`,
      {
        p_customer_id: params.customerId,
        p_asset_ids: params.assetIds,
        p_workflow_type: params.workflowType ?? 'issue',
      },
    );
    return response;
  }

  /**
   * Clear all items from the cart, releasing all reservations.
   */
  async clearCart(
    customerId: string,
    workflowType: WorkflowType = 'issue',
    options?: { allowActiveScan?: boolean },
  ): Promise<ClearCartResult> {
    const response = await httpClient.post<ClearCartResult>(
      `${this.baseUrl}/rpc/clear_workflow_cart`,
      {
        p_customer_id: customerId,
        p_workflow_type: workflowType,
        ...(options?.allowActiveScan ? { p_allow_active_scan: true } : {}),
      },
    );
    return response;
  }

  /**
   * Remove specific asset reservations from a cart (for partial submissions).
   */
  async removeReservationsByAssetIds(
    customerId: string,
    workflowType: WorkflowType,
    assetIds: string[],
  ): Promise<{ success: boolean; reservations_released: number }> {
    return httpClient.post(`${this.baseUrl}/rpc/remove_cart_reservations_by_asset_ids`, {
      p_customer_id: customerId,
      p_workflow_type: workflowType,
      p_asset_ids: assetIds,
    });
  }

  /**
   * Delete a cart by ID.
   */
  async deleteCart(cartId: string): Promise<void> {
    await httpClient.post(`${this.baseUrl}/rpc/delete_workflow_cart`, {
      p_cart_id: cartId,
    });
  }

  /**
   * Update the scanning status of a cart.
   */
  async updateScanningStatus(cartId: string, status: ScanningStatus): Promise<void> {
    await httpClient.patch(`${this.baseUrl}/workflow_carts?id=eq.${cartId}`, {
      scanning_status: status,
    });
  }

  /**
   * Persist a document number on a cart (used by maintenance-send workflow).
   */
  async updateDocumentNumber(cartId: string, documentNumber: string): Promise<void> {
    await httpClient.patch(`${this.baseUrl}/workflow_carts?id=eq.${cartId}`, {
      document_number: documentNumber,
    });
  }

  /**
   * Persist a maintenance request ID on a cart (used by maintenance-receive workflow).
   */
  async updateMaintenanceRequestId(cartId: string, maintenanceRequestId: string): Promise<void> {
    await httpClient.patch(`${this.baseUrl}/workflow_carts?id=eq.${cartId}`, {
      maintenance_request_id: maintenanceRequestId,
    });
  }

  /**
   * Cancel an active sync session.
   * Resets per-item scan progress and sets scanning_status back to not_started.
   */
  async cancelSync(cartId: string): Promise<void> {
    await httpClient.post(`${this.baseUrl}/rpc/cancel_cart_sync`, {
      p_cart_id: cartId,
    });
  }

  /**
   * Bulk allocate assets to cart by catalog_id.
   * Finds available assets matching the requested catalog_ids and creates reservations atomically.
   */
  async bulkAllocateToCart(params: BulkAllocateParams): Promise<BulkAllocateResult> {
    const response = await httpClient.post<BulkAllocateResult>(
      `${this.baseUrl}/rpc/bulk_allocate_to_cart`,
      {
        p_customer_id: params.customerId,
        p_items: params.items.map((item) => ({
          quantity: item.quantity,
          catalog_id: item.catalog_id,
          item_name: item.item_name,
          ie_kit_id: item.ie_kit_id ?? null,
          location_id: item.location_id ?? null,
        })),
        p_exclude_asset_ids: params.excludeAssetIds ?? [],
      },
    );
    return response;
  }

  /**
   * Upsert a size allocation cart placeholder for a parent catalog item.
   * Placeholders represent unsized items awaiting size selection and asset
   * allocation; they do not affect stock on their own.
   */
  async upsertSizeAllocationCart(params: {
    customerId: string;
    parentCatalogId: string;
    quantity: number;
  }): Promise<{
    success: boolean;
    cart_id: string | null;
    placeholder_id: string | null;
    quantity: number;
    error?: string;
  }> {
    const response = await httpClient.post<{
      success: boolean;
      cart_id: string | null;
      placeholder_id: string | null;
      quantity: number;
      error?: string;
    }>(`${this.baseUrl}/rpc/upsert_size_allocation_cart`, {
      p_customer_id: params.customerId,
      p_parent_catalog_id: params.parentCatalogId,
      p_quantity: params.quantity,
    });
    return response;
  }

  /**
   * Bulk upsert size allocation cart placeholders for multiple parent catalog items.
   * More efficient than calling upsertSizeAllocationCart multiple times.
   */
  async bulkUpsertSizeAllocationCart(params: {
    customerId: string;
    items: Array<{ parentCatalogId: string; quantity: number }>;
  }): Promise<{
    success: boolean;
    cart_id: string | null;
    created_count: number;
    updated_count: number;
    error?: string;
  }> {
    const response = await httpClient.post<{
      success: boolean;
      cart_id: string | null;
      created_count: number;
      updated_count: number;
      error?: string;
    }>(`${this.baseUrl}/rpc/bulk_upsert_size_allocation_cart`, {
      p_customer_id: params.customerId,
      p_items: params.items.map((item) => ({
        parent_catalog_id: item.parentCatalogId,
        quantity: item.quantity,
      })),
    });
    return response;
  }

  /**
   * Clear all size allocation cart placeholders for the given customer in the issue workflow.
   */
  async clearSizeAllocationCarts(customerId: string): Promise<{
    success: boolean;
    cart_id: string | null;
    cleared: number;
    error?: string;
  }> {
    const response = await httpClient.post<{
      success: boolean;
      cart_id: string | null;
      cleared: number;
      error?: string;
    }>(`${this.baseUrl}/rpc/clear_size_allocation_carts`, {
      p_customer_id: customerId,
    });
    return response;
  }
}
